import {filter, find, isEmpty, map, some, isBoolean, forEach, lowerCase, transform, intersection, isEqual} from 'lodash';
import {useCallback, useState, useEffect, useMemo, useContext, useRef} from 'react';
import {useTranslation} from 'react-i18next';

import {agentActionsApi, groupApi, SessionParams, sessionResultInformationApi} from 'api';
import {
    AssignmentSelectionModal, ConfirmModal, PredefinedNotifierType, notifier, SessionFeedbackModal,
    Initials, ClientDataModal, MediaDevicesDialog, PictureInPictureObserver,
    DeescalateDialog,
    AudioComplianceNotifier, audioComplianceNotifier,
} from 'components';
import {ROUTE_PATH} from 'constants/routes';
import {REMOTE_MEDIA_ID, SCREENSHOT_CANVAS_ID} from 'constants/session';
import {UserSessionContext, UserContext} from 'context';
import {history} from 'context/history';
import {
    AudioStreamState, SttClientState, PartialSessionChangeName, SocketEvent,
    VideoPinParticipant, VirtualBackgroundType, WebRTCServerSource
} from 'enums';
import {
    useUserSession, useApiData, useSocketEventCallback, useObjectDiff, useMediaDevices, useIsChangedTo, usePrevious,
    useUnmount,
} from 'hooks';
import {
    SessionActivityName, ScreenSharingActivityStatus, SessionActivity, AccountAssignmentInfo, SessionScreenshot, SessionGoal,
    AsyncRequestTypeName, SessionScreenshotSubject, ParticipantAsyncRequest, SessionActivityTextChatDataState,
    SessionGoalType, AgentSession, SessionFeedback, ReassignDetails, AnyParticipant, SessionActivityTextChatData,
    ClientDevice, WebRTCSpeechActivityData, InviteeParticipant, SessionActivityAgentChatbotData
} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {userActiveSessionStore, pictureInPictureStore, inSessionDataStore} from 'services';
import {
    TranslationSettings,
    ComplianceDecisionMeta,
    ComplianceHint,
    FilesAreaContent,
    OnlineStatus,
    PartialSessionChange,
    TextChatMessage,
    TextChatThreads,
    VirtualBackgroundConfig,
    WebRTCConnectDetails,
    VirtialBackgroundsData,
    TextChatContentType,
} from 'types';
import {sessionUtils, getScreenshotBase64, logger, apiUtils, mediaDevicesUtils, isMobile} from 'utils';

import {CoBrowsingStartDialog} from './CoBrowsingStartDialog';
import {ConflictingActivitiesModal} from './ConflictingActivitiesModal';
import {GoalsModal} from './GoalsModal';
import {IdAnalyzerFaceVerificationModal} from './IdAnalyzerFaceVerificationModal';
import {InviteModal} from './InviteModal';
import {ParticipantGeolocationDialog} from './ParticipantGeolocationDialog';
import {ReassignModal} from './ReassignModal';
import {ResultInformationDialog} from './ResultInformationDialog';
import {ScreenSharingDialog} from './ScreenSharingDialog';
import {ScreenshotPreviewModal} from './ScreenshotPreviewModal';
import {ScreenshotsSelectionModal} from './ScreenshotsSelectionModal';
import {SumaDocumentVerificationModal} from './SumaDocumentVerificationModal';
import {SumaEmailVerificationModal} from './SumaEmailVerificationModal';
import {SumaIPVerificationModal} from './SumaIPVerificationModal';
import {SumaPhoneVerificationModal} from './SumaPhoneVerificationModal';
import {UploadSelfieVerificationDialog} from './UploadSelfieVerificationDialog';
import {DevicePermission, FromInSessionContextProps, InSessionAction, SidebarView, UserSessionResponsePayload} from './types';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE;
const T_KEYS_ONLINE_STATE = TRANSLATION_KEYS.COMPONENTS.ONLINE_STATE;

const ASYNC_REQUESTS_TO_CONFIRM_ON_MOBILE = [
    AsyncRequestTypeName.UploadSelfieVerification,
    AsyncRequestTypeName.Geolocation,
];

type Props = {
    payload: UserSessionResponsePayload;
    contextProps: FromInSessionContextProps;
    isOpenedInNewWindow?: boolean;
    resolvedAsyncRequests: {
        request: ParticipantAsyncRequest;
        participantId: string;
    }[];
    cleanResolvedAsyncRequests: () => void;
    handleSessionPayload: (payload: UserSessionResponsePayload) => void;
    children: React.ReactElement;
}

export const SessionActions: React.FC<Props> = ({
    payload, contextProps, resolvedAsyncRequests, cleanResolvedAsyncRequests, handleSessionPayload, children, isOpenedInNewWindow
}: Props) => {
    const {socket} = useContext(UserContext);
    const {
        inProgressActions, sidebarView, onSidebarViewChange, handleInSessionAction, handleInSessionActionRequest,
        localSessionEnded, setLocalSessionEnded
    } = contextProps;
    const {
        session, isOnHold, sessionParams, webRTCActivities, screenSharingActivity, audioVideoActivity, textChatActivity, isStarted, isEnded,
        clientParticipant, isScreenshotsPreviewEnabled, localParticipant, canSendFeedback, feedbackAvailability, audioActivity, sipActivity,
        getActivityByName, keepTextChatOnRestart, getActivityById, videoPinnedParticipant: videoPinParticipantDefault,
        audioVideoViewConference, virtualBackgroundEnabled, guestEnabled, canManageSession, agentChatbotActivity
    } = useUserSession({payload});
    const prevIsStarted = usePrevious(isStarted);
    const {t} = useTranslation();
    const [initialized, setInitialized] = useState(false);
    const [audioStreamState, setAudioStreamState] = useState<AudioStreamState | SttClientState>(null);
    const prevTextChatActivity = usePrevious(textChatActivity);
    const prevAudioActivity = usePrevious(audioActivity);
    const prevAudioVideoActivity = usePrevious(audioVideoActivity);
    const prevAgentChatbotActivity = usePrevious(agentChatbotActivity);
    const [feedbackModalProps, setFeedbackModalProps] = useState({open: false, sent: false});
    const [videoPinParticipant, setVideoPinParticipant] = useState<VideoPinParticipant>(videoPinParticipantDefault);
    const [pinnedParticipantId, setPinnedParticipantId] = useState<string>();
    const [sendInviteModalParticipant, setSendInviteModalParticipant] = useState<AnyParticipant>(clientParticipant);
    const sendInviteParticipantConnected = !!sessionUtils.getParticipantById(session, sendInviteModalParticipant?.id)?.socket?.connected;
    const prevSendInviteParticipantConnected = usePrevious(sendInviteParticipantConnected);

    const [resultInformationDialogProps, setResultInformationDialogProps] = useState({
        open: false,
        success: false,
        values: []
    });

    const [screenSharingDialogOpen, setScreenSharingDialogOpen] = useState(false);
    const [inviteModalOpen, setInviteModalOpen] = useState(false);
    const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
    const [changeAssignmentDialogOpen, setChangeAssignmentDialogOpen] = useState(false);
    const [goalsModalOpen, setGoalsModalOpen] = useState(false);
    const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
    const [feedbackModalAutoOpened, setFeedbackModalAutoOpened] = useState(false);
    const [updateParticipantModalProps, setUpdateParticipantModalProps] = useState<{
        open: boolean,
        participant?: AnyParticipant
    }>({open: false});
    const [mediaDevicesDialogOpen, setMediaDevicesDialogOpen] = useState(false);
    const [deescalateDialogOpen, setDeescalateDialogOpen] = useState(false);
    const [deescalated, setDeescalated] = useState(false);
    const isDeescalated = useRef(false);
    const [goalToConfirm, setGoalToConfirm] = useState<SessionGoal>(null);
    const [currentMediaDevices, setCurrentMediaDevices] = useMediaDevices();

    const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
    const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
    const [tracks, setTracks] = useState<MediaStreamTrack[]>([]);
    const [createAsyncRequestDialogProps, setCreateAsyncRequestDialogProps] = useState<{
        type: AsyncRequestTypeName,
        participantId: string;
    }>(null);
    const [asyncRequestsDialogQueueProps, setAsyncRequestsDialogQueueProps] = useState<{
        type: AsyncRequestTypeName,
        participantId: string;
    }[]>([]);
    const [conflictingActivitiesModalProps, setConflictingActivitiesModalProps] = useState<{
        open: boolean,
        activity?: SessionActivityName,
        conflictingActivities?: SessionActivityName[]
    }>({open: false});
    const [selectedScreenshot, setSelectedScreenshot] = useState<SessionScreenshot>();
    const [deleteScreenshotCandidate, setDeleteScreenshotCandidate] = useState<SessionScreenshot>();
    const activeAsyncRequestDialog = useMemo(() => asyncRequestsDialogQueueProps[0], [asyncRequestsDialogQueueProps]);
    const activeAsyncRequest = useMemo(() => {
        if (!activeAsyncRequestDialog) {
            return null;
        }
        const {type, participantId} = activeAsyncRequestDialog;
        return find(sessionUtils.getParticipantById(session, participantId)?.asyncRequests, {type});
    }, [session, activeAsyncRequestDialog]);
    const [confirmStopActivityDialogProps, setConfirmStopActivityDialogProps] = useState<{
            open: boolean,
            title?: string,
            content?: string,
            data?: any,
    }>();
    const [coBrowseStartDialogProps, setCoBrowseStartDialogProps] = useState<{
        open: boolean,
        url?: string,
        isError?: boolean;
        isLoading?: boolean;
    }>();
    const [audioEnabled, setAudioEnabled] = useState(true);
    const [videoEnabled, setVideoEnabled] = useState(true);
    const [fullScreenVideo, setFullScreenVideo] = useState(false);
    const [screenshotsPreview, setScreenshotsPreview] = useState(false);
    const [participantsNetworkQuality, setParticipantsNetworkQuality] = useState<{[participantId: string]: number}>({});
    const [textChatThreads, setTextChatThreads] = useState<TextChatThreads>();
    const [subtitlesThreads, setSubtitlesThreads] = useState<TextChatThreads>();
    const [agentChatbotThreads, setAgentChatbotThreads] = useState<TextChatThreads>();
    const [virtualBackgroundConfig, setVirtualBackgroundConfig] = useState<VirtualBackgroundConfig>({type: VirtualBackgroundType.None});
    const [virtualBackgroundContent, setVirtualBackgroundContent] = useState<FilesAreaContent>();

    // The socket callback below is registered once, so it reads the activity through a ref to stay current
    const agentChatbotActivityRef = useRef(agentChatbotActivity);
    agentChatbotActivityRef.current = agentChatbotActivity;

    const isScreenSharingRejected = useIsChangedTo(screenSharingActivity?.data?.status, ScreenSharingActivityStatus.Rejected);

    const participantsStatuses = useMemo(() => transform(session.participants, (currentStatuses, participant) => {
        const lastActivityDate = sessionUtils.getParticipantLastActivity(session, participant?.id);
        const currentDate = new Date();
        const currentStatus = sessionUtils.getOnlineStatus(session, currentDate, participant, lastActivityDate);
        currentStatuses[participant.id] = currentStatus === OnlineStatus.Online;
    }, {}), [session]);

    const participantsOnlineStatusChanges = useObjectDiff(participantsStatuses, participantsStatuses);

    const isSipOrPhoneClient = useMemo(() =>
        [ClientDevice.Sip, ClientDevice.Phone].includes(clientParticipant?.data.device),
    [clientParticipant?.data.device]
    );

    const {data: failedResultValues, send: fetchFailedResultValues} =
        useApiData<string[], [AccountAssignmentInfo, boolean]>(
            sessionResultInformationApi.getFailedSessionResultValues
        );

    const {data: successResultValues, send: fetchSuccessResultValues} =
        useApiData<string[], [AccountAssignmentInfo, boolean]>(
            sessionResultInformationApi.getSuccessSessionResultValues
        );

    const onFetchVirtualBackgroundsSuccess = useCallback((virtualBackgrounds: VirtialBackgroundsData) => {
        setVirtualBackgroundConfig(virtualBackgrounds.config);
        setVirtualBackgroundContent(virtualBackgrounds.content);
    }, []);

    const {send: fetchVirtualBackgrounds} =
        useApiData<VirtialBackgroundsData, [SessionParams]>(
            agentActionsApi.getVirtualBackgrounds,
            {
                onSuccess: onFetchVirtualBackgroundsSuccess
            }
        );

    const onChangeAssignmentSuccess = useCallback(() => {
        setChangeAssignmentDialogOpen(false);
    }, []);

    const {send: sendChangeAssignment, loading: changeAssignmentLoading} = useApiData(agentActionsApi.changeAssignment,
        {onSuccess: onChangeAssignmentSuccess, notificationType: PredefinedNotifierType.Save});

    const {send: fetchGroups, data: groups} = useApiData(groupApi.getGroups);

    const onParticipantUpdateSuccess = useCallback(() => {
        setUpdateParticipantModalProps({open: false});
    }, []);

    const {
        send: sendUpdateParticipant,
        loading: updateParticipantLoading,
        error: updateParticipantError
    } = useApiData(agentActionsApi.updateParticipantData,
        {onSuccess: onParticipantUpdateSuccess, notificationType: PredefinedNotifierType.Save});

    const {
        send: requestAiSuggestionsFn,
        data: aiSuggestions,
        loading: aiSuggestionsLoading,
        error: aiSuggestionsError
    } = useApiData(agentActionsApi.getAiSuggestions);

    const requestAiSuggestions = useCallback(() => {
        requestAiSuggestionsFn(sessionParams, {activityName: SessionActivityName.TextChat});
    }, [requestAiSuggestionsFn, sessionParams]);

    const {
        send: requestAiSuggestionsAudioFn,
        data: aiSuggestionsAudio,
        loading: aiSuggestionsAudioLoading,
        error: aiSuggestionsAudioError,
        reset: aiSuggestionsAudioReset
    } = useApiData(agentActionsApi.getAiSuggestions);

    const requestAiSuggestionsAudio = useCallback(() => {
        requestAiSuggestionsAudioFn(sessionParams, {activityName: audioActivity?.name || audioVideoActivity?.name});
    }, [requestAiSuggestionsAudioFn, sessionParams, audioActivity, audioVideoActivity]);

    const askAgentChatbot = useCallback((question: string): Promise<any> => {
        const activity = getActivityByName(SessionActivityName.AgentChatbot);
        if (!activity) {
            return null;
        }
        return agentActionsApi.askAgentChatbot(sessionParams, {activityId: activity.id, question})
            .catch((error) => {
                handleInSessionActionRequest(Promise.reject(error));
            });
    }, [getActivityByName, sessionParams, handleInSessionActionRequest]);

    useSocketEventCallback(socket, SocketEvent.ActiveSessionChanged, useCallback((_session: AgentSession) => {
        logger.debug(`Received socket event: ${SocketEvent.ActiveSessionChanged}. Version: ${ _session?.version}`, _session);
        handleSessionPayload({session: _session});
    }, [handleSessionPayload]));

    useSocketEventCallback(socket, SocketEvent.ComplianceHint, useCallback((hint: ComplianceHint) => {
        if (!hint || hint.sessionId !== session?.id || isEnded || localSessionEnded) {
            return;
        }
        audioComplianceNotifier.show(hint);
    }, [session?.id, isEnded, localSessionEnded]));

    useSocketEventCallback(socket, SocketEvent.ActiveSessionPartiallyChanged, useCallback((change: PartialSessionChange) => {
        logger.debug(`Received socket event: ${SocketEvent.ActiveSessionPartiallyChanged}`, change);
        if (change.name === PartialSessionChangeName.TextChat && change.data) {
            setTextChatThreads((prev) => {
                const oldMessages = prev?.[change.data.threadId] || [];
                const newMessages = [...oldMessages, change.data.message];
                const newThread = {...(prev || {}), [change.data.threadId]: newMessages};
                return newThread;
            });
        } else if (change.name === PartialSessionChangeName.AgentChatbot && change.data) {
            // A reply can arrive after the activity was stopped — appending it would refill the thread right
            // after it was cleared and leak the old conversation into the next chatbot activity
            if (!agentChatbotActivityRef.current) {
                return;
            }
            setAgentChatbotThreads((prev) => {
                const oldMessages = prev?.[change.data.threadId] || [];
                const newMessages = [...oldMessages, change.data.message];
                return {...(prev || {}), [change.data.threadId]: newMessages};
            });
        } else if ([PartialSessionChangeName.AudioVideo, PartialSessionChangeName.Audio].includes(change.name)) {
            setSubtitlesThreads((prev) => {
                const prevMessages = prev?.[change.data.threadId] || [];
                const existingMessage = find(prevMessages, (m) => m.id === change.data.message.id);
                if (existingMessage) {
                    const updatedMessages = map(prevMessages, (m) => {
                        if (m.id === change.data.message.id) {
                            return {
                                ...m,
                                content: change.data.message.content
                            } as TextChatMessage;
                        }
                        return m;
                    });

                    return {
                        ...prev,
                        [change.data.threadId]: updatedMessages
                    };
                } else {
                    return {
                        ...prev,
                        [change.data.threadId]: [...prevMessages, change.data.message]
                    };
                }
            });
        }
    }, []));

    /** Start of "activities" */
    const startActivity = useCallback((name: SessionActivityName, data?: any) => {
        handleInSessionActionRequest(agentActionsApi.startActivity(sessionParams, name, data),
            InSessionAction.ToggleActivity, {activityName: name}).then(() => {
            if (name === SessionActivityName.TextChat) {
                onSidebarViewChange(SidebarView.TextChat);
            }

            if (name === SessionActivityName.AgentChatbot) {
                onSidebarViewChange(SidebarView.AgentChatbot);
            }

            if (name === SessionActivityName.AudioVideo || name === SessionActivityName.Audio || name === SessionActivityName.SIP) {
                setAudioEnabled(true);
            }

            if (name === SessionActivityName.AudioVideo) {
                setVideoEnabled(true);
            }

            if (name === SessionActivityName.Audio || SessionActivityName.AudioVideo && sidebarView === SidebarView.Transcriptions) {
                onSidebarViewChange(null);
            }
        });
    }, [handleInSessionActionRequest, sessionParams, sidebarView, onSidebarViewChange]);

    const stopActivity = useCallback((id: string) => {
        const activity = getActivityById(id);
        handleInSessionActionRequest(agentActionsApi.stopActivity(sessionParams, id),
            InSessionAction.ToggleActivity, {activityName: activity?.name, activityId: id});
    }, [sessionParams, handleInSessionActionRequest, getActivityById]);

    const onBeforeStartActivity = useCallback((name: SessionActivityName) => {
        if (name === SessionActivityName.CoBrowsing) {
            setCoBrowseStartDialogProps({open: true, url: 'https://'});
        } else if (name === SessionActivityName.ScreenSharing) {
            setScreenSharingDialogOpen(true);
        } else {
            startActivity(name);
        }
    }, [startActivity]);

    const checkConflictingActivities = useCallback((activityName: SessionActivityName) => {
        const conflictingActivities = session.conflictingActivities[activityName];
        const runningConflictingActivities = intersection(conflictingActivities, map(session.activities, ({name}) => name));
        if (!isEmpty(runningConflictingActivities)) {
            setConflictingActivitiesModalProps({
                open: true,
                activity: activityName,
                conflictingActivities: runningConflictingActivities
            });
        } else {
            onBeforeStartActivity(activityName);
        }
    }, [session, onBeforeStartActivity]);

    const handleConflictingActivitiesModalClose = useCallback((confirmed: boolean, activity: SessionActivityName) => {
        if (confirmed) {
            onBeforeStartActivity(activity);
        }
        setConflictingActivitiesModalProps((prev) => ({...prev, open: false}));
    }, [onBeforeStartActivity]);

    const onBeforeStopActivity = useCallback((activity: SessionActivity) => {
        let content = null;
        if (activity.name === SessionActivityName.TextChat && !keepTextChatOnRestart) {
            content = t(T_KEYS.CONFIRM_STOP_ACTIVITY_MODAL.CONTENT.TEXT_CHAT);
        } else if (activity.name === SessionActivityName.SIP) {
            content = t(T_KEYS.CONFIRM_STOP_ACTIVITY_MODAL.CONTENT.SIP);
        }
        if (activity.name === SessionActivityName.ScreenSharing) {
            const status = screenSharingActivity?.data?.status;
            if (status === ScreenSharingActivityStatus.Rejected) {
                notifier.error({details: t(T_KEYS.WEBRTC.SCREEN_SHARING.REJECTED)});
                stopActivity(activity.id);
                return;
            } else if (status === ScreenSharingActivityStatus.Requested) {
                stopActivity(activity.id);
                return;
            }
        }

        setConfirmStopActivityDialogProps({
            open: true,
            title: t(T_KEYS.CONFIRM_STOP_ACTIVITY_MODAL.TITLE),
            content,
            data: activity
        });
    }, [t, keepTextChatOnRestart, stopActivity, screenSharingActivity]);

    const onCloseConfirmStopActivityDialog = useCallback((confirm: boolean, activity: SessionActivity) => {
        if (confirm) {
            stopActivity(activity.id);
            if (activity.name === SessionActivityName.TextChat && sidebarView === SidebarView.TextChat) {
                onSidebarViewChange(null);
            } else if (activity.name === SessionActivityName.AgentChatbot && sidebarView === SidebarView.AgentChatbot) {
                onSidebarViewChange(null);
            } else if (activity.name === SessionActivityName.SIP && sidebarView === SidebarView.SIP) {
                onSidebarViewChange(null);
            } else if (activity.name === SessionActivityName.AudioVideo && sidebarView === SidebarView.VirtualBackground) {
                onSidebarViewChange(null);
            } else if ((activity.name === SessionActivityName.AudioVideo || activity.name === SessionActivityName.Audio)) {
                if (sidebarView === SidebarView.Transcriptions) {
                    onSidebarViewChange(null);
                }
                if (aiSuggestionsAudio) {
                    aiSuggestionsAudioReset();
                }
            }
        }
        setConfirmStopActivityDialogProps((prev) => ({...prev, open: false}));
    }, [stopActivity, sidebarView, onSidebarViewChange, aiSuggestionsAudio, aiSuggestionsAudioReset]);

    const toggleActivity = useCallback((name: SessionActivityName) => {
        const activity = getActivityByName(name);
        if (activity) {
            onBeforeStopActivity(activity);
        } else {
            checkConflictingActivities(name);
        }
    }, [getActivityByName, checkConflictingActivities, onBeforeStopActivity]);

    const notifyWebRTCActivities =
        useCallback((source: WebRTCServerSource, status: 'success' | 'error', details?: WebRTCConnectDetails) => {
            sessionUtils.filterWebRTCActivitiesBySource(webRTCActivities, source).forEach((activity) => {
                const data = {
                    connection: {
                        ...(details || {}),
                        status
                    }
                };
                agentActionsApi.addActivityData(sessionParams, activity.id, data);
            });
        }, [webRTCActivities, sessionParams]);

    const requestUpdateParticipantDevicePermission = useCallback((
        participantId: string, devicePermission: DevicePermission, disabled: boolean
    ) => {
        const activity = audioVideoActivity || audioActivity;
        return handleInSessionActionRequest(
            agentActionsApi.addActivityData(sessionParams, activity.id, {
                participantId,
                devicePermission: {[devicePermission]: {disabled}}
            }),
            InSessionAction.ToggleParticipantDevicePermission,
            {devicePermission, participantId}
        );
    }, [audioVideoActivity, audioActivity, sessionParams, handleInSessionActionRequest]);

    const notifyWebRTCStartSuccess = useCallback((source: WebRTCServerSource, connectStatus?: WebRTCConnectDetails) => {
        notifyWebRTCActivities(source, 'success', connectStatus);
    }, [notifyWebRTCActivities]);

    const notifyWebRTCStartFailed = useCallback((source: WebRTCServerSource, error: WebRTCConnectDetails) => {
        notifyWebRTCActivities(source, 'error', error);
    }, [notifyWebRTCActivities]);

    const setScreenSharingActivityStatus = useCallback((status: ScreenSharingActivityStatus) => {
        if (!screenSharingActivity) {
            return null;
        }
        const data = {
            ...screenSharingActivity.data,
            status
        };

        return handleInSessionActionRequest(agentActionsApi.addActivityData(sessionParams, screenSharingActivity.id, data));
    }, [screenSharingActivity, sessionParams, handleInSessionActionRequest]);

    const onStopScreenSharing = useCallback((error: {message: string, isCanceled?: boolean}) => {
        if (error?.message) {
            notifier.error({details: error.message});
            stopActivity(screenSharingActivity.id);
        }
    }, [screenSharingActivity, stopActivity]);

    const setMediaDevices = useCallback((devices: MediaDeviceInfo[]) => {
        setVideoDevices(mediaDevicesUtils.filterCameras(devices));
        setAudioDevices(mediaDevicesUtils.filterMicrophones(devices));
    }, []);

    const onToggleAudio = useCallback(() => {
        setAudioEnabled((prev) => !prev);
    }, []);

    const onToggleVideo = useCallback(() => {
        setVideoEnabled((prev) => !prev);
    }, []);

    const onToggleFullScreenVideo = useCallback((value?: boolean) => {
        if (isBoolean(value)) {
            setFullScreenVideo(value);
        } else {
            setFullScreenVideo((prev) => !prev);
        }
    }, []);

    const onTogglePictureInPicture = useCallback(() => {
        if (!pictureInPictureStore.getValue().active) {
            const containerSelector = audioVideoViewConference ? sessionUtils.getParticipantVideoSelector(
                clientParticipant.id
            ) : `#${REMOTE_MEDIA_ID}`;
            pictureInPictureStore.mergeValue({container: containerSelector, enabled: true});
        } else {
            pictureInPictureStore.mergeValue({container: null, enabled: false});
        }
    }, [clientParticipant, audioVideoViewConference]);

    const onLocalNetworkQualityLevelChange = useCallback((level: number) => {
        setParticipantsNetworkQuality((prev) => ({...prev, [localParticipant.id]: level}));
    }, [localParticipant]);

    const onRemoteNetworkQualityLevelChange = useCallback((level: number) => {
        if (clientParticipant) {
            setParticipantsNetworkQuality((prev) => ({...prev, [clientParticipant.id]: level}));
        }
    }, [clientParticipant]);

    const onScreenSharingDialogClose = useCallback((presenterId: string) => {
        if (presenterId) {
            startActivity(SessionActivityName.ScreenSharing, {
                presenterId,
                status: ScreenSharingActivityStatus.Requested
            });
        }
        setScreenSharingDialogOpen(false);
    }, [startActivity]);

    const onCoBrowsingStartDialogClose = useCallback(async (url: string) => {
        if (url) {
            try {
                setCoBrowseStartDialogProps({open: true, isLoading: true});
                await startActivity(SessionActivityName.CoBrowsing, {url});
                setCoBrowseStartDialogProps((prev) => ({...prev, open: false}));
            } catch (error) {
                setCoBrowseStartDialogProps({open: true, isError: true});
            }
        } else {
            setCoBrowseStartDialogProps((prev) => ({...prev, open: false}));
        }
    }, [startActivity]);

    const onTextChatNewMessage = useCallback(() => {
        setHasUnreadMessages(true);
    }, []);

    const onTextChatFileDownload = useCallback(async (fileName: string, activityId: string): Promise<any> => {
        const base64 = await agentActionsApi.getActivityFile(sessionParams, {fileName, activityId});
        apiUtils.saveFileFromBase64(fileName, base64);
    }, [sessionParams]);

    const onTextChatSubmitMessage = useCallback((message: Partial<TextChatMessage>, compliance?: ComplianceDecisionMeta): Promise<any> => {
        if (!textChatActivity) {
            return null;
        }

        return agentActionsApi.addActivityData(sessionParams, textChatActivity.id,
            {message, ...(compliance && {compliance})}).catch((error) => {
            // to allow text chat handler to handle an error
            if (message.contentType === TextChatContentType.File || apiUtils.isFileTypeError(error)) {
                throw error;
            } else {
                handleInSessionActionRequest(Promise.reject(error));
            }
        });
    }, [sessionParams, textChatActivity, handleInSessionActionRequest]);

    const onTextChatChangeState = useCallback((state: SessionActivityTextChatDataState) => {
        if (!textChatActivity) {
            return null;
        }
        handleInSessionActionRequest(agentActionsApi.addActivityData(sessionParams, textChatActivity.id, {state}));
    }, [sessionParams, textChatActivity, handleInSessionActionRequest]);

    const isDeescalating = useMemo(
        () => some(inProgressActions, (a) => a.action === InSessionAction.Deescalate), [inProgressActions]
    );

    const deescalate = useCallback((confirm: boolean, data?: {context: string}) => {
        if (confirm) {
            handleInSessionActionRequest(agentActionsApi.deescalate(sessionParams, data), InSessionAction.Deescalate)
                .then(() => {
                    isDeescalated.current = true;
                    setDeescalateDialogOpen(false);
                    setDeescalated(true);
                    setLocalSessionEnded(true);
                });
        } else {
            setDeescalateDialogOpen(false);
        }
    }, [sessionParams, handleInSessionActionRequest, setLocalSessionEnded]);

    const onActivityParticipantLanguageChange = useCallback((activity: SessionActivity, participantId: string, language: string) => {
        if (!activity) {
            return null;
        }
        handleInSessionActionRequest(agentActionsApi.addActivityData(
            sessionParams, activity.id, {translationSettings: {participantId, language}}));
    }, [sessionParams, handleInSessionActionRequest]);

    const onAudioStreamingToggle = useCallback((activityId: string, value: boolean) =>
        agentActionsApi.addActivityData(sessionParams, activityId, {streamAudio: {enabled: value}}),
    [sessionParams]);

    useEffect(() => {
        if (screenSharingActivity && isScreenSharingRejected) {
            notifier.error({details: t(T_KEYS.WEBRTC.SCREEN_SHARING.REJECTED)});
            stopActivity(screenSharingActivity.id);
        }
    }, [isScreenSharingRejected, screenSharingActivity, stopActivity, t]);

    useEffect(() => {
        const textChatActivityData = textChatActivity?.data as SessionActivityTextChatData;
        if (textChatActivity && !prevTextChatActivity && !textChatThreads) {
            setTextChatThreads(textChatActivityData?.threads);
        } else if (!textChatActivity && prevTextChatActivity && textChatThreads) {
            setTextChatThreads(null);
        }
    }, [prevTextChatActivity, textChatActivity, textChatThreads]);

    useEffect(() => {
        const agentChatbotActivityData = agentChatbotActivity?.data as SessionActivityAgentChatbotData;
        if (agentChatbotActivity && !prevAgentChatbotActivity && !agentChatbotThreads) {
            setAgentChatbotThreads(agentChatbotActivityData?.threads);
        } else if (!agentChatbotActivity && prevAgentChatbotActivity && agentChatbotThreads) {
            setAgentChatbotThreads(null);
        }
    }, [prevAgentChatbotActivity, agentChatbotActivity, agentChatbotThreads]);

    useEffect(() => {
        if (!agentChatbotActivity && prevAgentChatbotActivity && sidebarView === SidebarView.AgentChatbot) {
            onSidebarViewChange(null);
        }
    }, [agentChatbotActivity, prevAgentChatbotActivity, sidebarView, onSidebarViewChange]);

    useEffect(() => {
        const subtitlesActivityData = audioActivity?.data as WebRTCSpeechActivityData;
        if (audioActivity && !prevAudioActivity && !subtitlesThreads) {
            setSubtitlesThreads(subtitlesActivityData?.transcription);
        } else if (!audioActivity && prevAudioActivity && subtitlesThreads) {
            setSubtitlesThreads(null);
        }
    }, [prevAudioActivity, audioActivity, subtitlesThreads]);

    useEffect(() => {
        const subtitlesActivityData = audioVideoActivity?.data as WebRTCSpeechActivityData;
        if (audioVideoActivity && !prevAudioVideoActivity && !subtitlesThreads) {
            setSubtitlesThreads(subtitlesActivityData?.transcription);
        } else if (!audioVideoActivity && prevAudioVideoActivity && subtitlesThreads) {
            setSubtitlesThreads(null);
        }
    }, [prevAudioVideoActivity, audioVideoActivity, subtitlesThreads]);

    /** End of "activities" */

    const toggleOnHold = useCallback(() => {
        handleInSessionActionRequest(
            isOnHold ? agentActionsApi.stopOnHold(sessionParams) : agentActionsApi.startOnHold(sessionParams),
            InSessionAction.ToggleOnHold);
    }, [isOnHold, sessionParams, handleInSessionActionRequest]);

    const onCloseReassignModal = useCallback((confirm: boolean, details: Partial<ReassignDetails>) => {
        setReassignDialogOpen(false);
        if (confirm) {
            handleInSessionActionRequest(agentActionsApi.reassignSession(sessionParams, details), InSessionAction.Reassign);
        }
    }, [sessionParams, handleInSessionActionRequest]);
    const reassign = useCallback(() => {
        setReassignDialogOpen(true);
    }, []);

    const changeAssignment = useCallback(() => {
        setChangeAssignmentDialogOpen(true);
    }, []);
    const onCloseChangeAssignmentModal = useCallback((confirm: boolean, assignment?: AccountAssignmentInfo) => {
        if (confirm) {
            sendChangeAssignment(sessionParams, assignment);
        } else {
            setChangeAssignmentDialogOpen(false);
        }
    }, [sessionParams, sendChangeAssignment]);

    const onParticipantUpdate = useCallback((participant: AnyParticipant) => {
        setUpdateParticipantModalProps({open: true, participant});
    }, []);

    const onParticipantRemove = useCallback((participant: AnyParticipant) => {
        agentActionsApi.removeParticipant(sessionParams, participant.id);
    }, [sessionParams]);

    const onCloseUpdateParticipantModal = useCallback((confirm: boolean, data?: any) => {
        if (confirm) {
            sendUpdateParticipant(sessionParams, data);
        } else {
            setUpdateParticipantModalProps({open: false});
        }
    }, [sendUpdateParticipant, sessionParams]);

    const toggleGoalsModal = useCallback(() => {
        setGoalsModalOpen((prev) => !prev);
    }, []);

    const handleParticipantInvite = useCallback((participantId: string, type: string, contact: string) =>
        agentActionsApi.sendParticipantInviteNotification(sessionParams, {participantId, type, contact}), [sessionParams]);

    const openInviteModal = useCallback((participant?: AnyParticipant) => {
        setSendInviteModalParticipant(participant);
        setInviteModalOpen(true);
    }, []);

    const onWebRTCConnectError = useCallback(() => {
        notifier.error({title: t(T_KEYS.WEBRTC.START_ACTIVITY_ERROR.TITLE), details: t(T_KEYS.WEBRTC.START_ACTIVITY_ERROR.DETAILS)});
    }, [t]);

    /** Start of "end session" */
    const endSession = useCallback((success: boolean, information?: string) => {
        handleInSessionActionRequest(agentActionsApi.endCall(sessionParams, {success, information}));
    }, [sessionParams, handleInSessionActionRequest]);

    const leaveSession = useCallback(() => {
        handleInSessionActionRequest(agentActionsApi.leaveSession(sessionParams));
        setLocalSessionEnded(true);
    }, [sessionParams, handleInSessionActionRequest, setLocalSessionEnded]);

    const onSuccessAddScreenshot = useCallback(() => {
        setFeedbackModalProps({open: false, sent: true});
        if (isDeescalated.current) {
            history.push(ROUTE_PATH.USER.ONLINE._SELF);
        }
    }, []);
    const {send: addFeedback, loading: addFeedbackLoading} =
        useApiData(agentActionsApi.addFeedback, {onSuccess: onSuccessAddScreenshot, notificationType: PredefinedNotifierType.Send});

    const {send: updateVirtualBackgroundSettings} = useApiData(agentActionsApi.updateVirtualBackground);

    const handleResultInformationDialogClose = useCallback((confirm: boolean, status: string): void => {
        if (confirm) {
            endSession(resultInformationDialogProps.success, status);
        }
        setResultInformationDialogProps({
            open: false,
            success: false,
            values: []
        });
    }, [endSession, resultInformationDialogProps]);

    const requestEndSession = useCallback((data?: {success: boolean}) => {
        if (data?.success) {
            if (successResultValues?.length) {
                setResultInformationDialogProps({
                    open: true,
                    success: data?.success,
                    values: successResultValues,
                });
            } else {
                endSession(true);
            }
        } else if (!failedResultValues?.length || !isStarted) {
            endSession(false);
        } else {
            setResultInformationDialogProps({
                open: true,
                success: data?.success,
                values: failedResultValues
            });
        }
    }, [isStarted, successResultValues, failedResultValues, endSession]);

    const updateTranscriptionSettings = useCallback((activityId: string, data: TranslationSettings) =>
        agentActionsApi.addActivityData(sessionParams, activityId, {translationSettings: data}), [sessionParams]);

    const toggleFeedbackModal = useCallback(() => {
        setFeedbackModalProps((prev) => ({...prev, open: !prev.open}));
    }, []);

    const onCloseFeedbackModal = useCallback((confirm: boolean, feedback: SessionFeedback) => {
        if (confirm) {
            addFeedback(sessionParams, {feedback});
        } else {
            setFeedbackModalProps((prev) => ({...prev, open: false}));
            if (isDeescalated.current) {
                history.push(ROUTE_PATH.USER.ONLINE._SELF);
            }
        }
    }, [sessionParams, addFeedback]);
    /** End of "end session" */

    const takeScreenshot = useCallback((screenshot: SessionScreenshot, targetParticipantId?: string) => {
        if (some(inProgressActions, (a) => a.action === InSessionAction.ToggleScreenshot && a.data.screenshotName === screenshot.name)) {
            return;
        }

        if (isMobile() && goalToConfirm?.data?.screenshotName !== screenshot.name) {
            setGoalToConfirm({type: SessionGoalType.Screenshot, data: {screenshotName: screenshot.name}});
            return;
        }

        const participantId = screenshot.subject === SessionScreenshotSubject.ParticipantVideo && audioVideoViewConference ?
            (targetParticipantId || clientParticipant?.id) : null;

        handleInSessionAction(getScreenshotBase64(screenshot.subject, participantId),
            InSessionAction.ToggleScreenshot, {screenshotName: screenshot.name, participantId})
            .then((base64: string) => handleInSessionActionRequest(
                agentActionsApi.uploadScreenShot(sessionParams, base64, screenshot.name, participantId),
                InSessionAction.ToggleScreenshot, {screenshotName: screenshot.name, participantId}
            )).catch((error) => {
                logger.error(error);
                notifier.error({title: screenshot.name, details: t(T_KEYS.MESSAGES.SCREENSHOT_UPLOAD_FAILED)});
            });
    }, [t, goalToConfirm, sessionParams, inProgressActions, clientParticipant, audioVideoViewConference,
        handleInSessionAction, handleInSessionActionRequest]);

    const onScreenshotClick = useCallback((screenshot: SessionScreenshot, participantId?: string) => {
        if (screenshot.received) {
            if (isScreenshotsPreviewEnabled) {
                setSelectedScreenshot(screenshot);
            }
            return;
        }

        takeScreenshot(screenshot, participantId);
    }, [takeScreenshot, isScreenshotsPreviewEnabled]);

    const onCloseScreenshotPreviewModal = useCallback(() => setSelectedScreenshot(null), []);

    const loadScreenshotPreview = useCallback((screenshot: SessionScreenshot) =>
        agentActionsApi.getScreenShot(sessionParams, screenshot.name), [sessionParams]);

    const onRequestDeleteScreenShot = useCallback((screenshot: SessionScreenshot) => {
        setDeleteScreenshotCandidate(screenshot);
    }, []);

    const onDeleteScreenShot = useCallback((screenshot: SessionScreenshot) =>
        agentActionsApi.deleteScreenShot(sessionParams, screenshot.name), [sessionParams]);
    const deleteFn = useCallback(() =>
        handleInSessionActionRequest(
            onDeleteScreenShot(deleteScreenshotCandidate),
            InSessionAction.ToggleScreenshot,
            {screenshotName: deleteScreenshotCandidate.name}
        )
    , [deleteScreenshotCandidate, onDeleteScreenShot, handleInSessionActionRequest]);
    const onSuccessScreenshotDelete = useCallback(() => {
        if (deleteScreenshotCandidate === selectedScreenshot) {
            setSelectedScreenshot(null);
        }
    }, [deleteScreenshotCandidate, selectedScreenshot]);
    const {send: deleteScreenshot, loading: deleteScreenshotInProgress} =
        useApiData(deleteFn, {onSuccess: onSuccessScreenshotDelete, notificationType: PredefinedNotifierType.Delete});

    const onToggleScreenshotsPreview = useCallback(() => {
        setScreenshotsPreview((prev) => !prev);
    }, []);

    const onCloseDeleteScreenshotDialog = useCallback((confirm: boolean) => {
        if (confirm) {
            deleteScreenshot();
        }
        setDeleteScreenshotCandidate(null);
    }, [deleteScreenshot]);

    const canMakeScreenshot = useCallback((screenshotName: string) => {
        const screenshot = find(session.screenshots, {name: screenshotName});
        return screenshot && (screenshot?.subject !== SessionScreenshotSubject.ParticipantVideo || !!audioVideoActivity);
    }, [session, audioVideoActivity]);
    /** End of "screenshots" */

    /** Start of "Async requests" */
    const sendCreateParticipantAsyncRequest = useCallback((participantId: string, requestType: AsyncRequestTypeName, data?: any) => {
        handleInSessionActionRequest(
            agentActionsApi.createParticipantAsyncRequest(sessionParams, {participantId, requestType, ...(data && data)}),
            InSessionAction.ToggleAsyncRequest,
            {participantId, asyncRequestType: requestType}
        );
    }, [sessionParams, handleInSessionActionRequest]);

    const executeUploadSelfieVerification = useCallback((participantId: string) => {
        if (some(inProgressActions, (a) =>
            a.action === InSessionAction.ToggleAsyncRequest &&
            a.data?.participantId === participantId &&
            a.data?.asyncRequestType === AsyncRequestTypeName.UploadSelfieVerification
        )) {
            return;
        }

        handleInSessionAction(
            getScreenshotBase64(SessionScreenshotSubject.LocalScreen),
            InSessionAction.ToggleAsyncRequest,
            {participantId, asyncRequestType: AsyncRequestTypeName.UploadSelfieVerification}
        ).then((base64: string) => {
            sendCreateParticipantAsyncRequest(participantId, AsyncRequestTypeName.UploadSelfieVerification, {data: {base64}});
        });
    }, [inProgressActions, handleInSessionAction, sendCreateParticipantAsyncRequest]);

    const openVerificationScreenshotsModal = useCallback((participantId: string, asyncRequestType: AsyncRequestTypeName) => {
        setCreateAsyncRequestDialogProps({type: asyncRequestType, participantId});
    }, []);
    const onCloseVerificationScreenshotsModal = useCallback((confirmed: boolean, params: any, type: AsyncRequestTypeName) => {
        if (confirmed) {
            sendCreateParticipantAsyncRequest(
                createAsyncRequestDialogProps.participantId, type, {params});
        }
        setCreateAsyncRequestDialogProps(null);
    }, [createAsyncRequestDialogProps, sendCreateParticipantAsyncRequest]);

    const isAsyncRequestConfirmGoal = useCallback((participantId: string, requestType: AsyncRequestTypeName) =>
        goalToConfirm?.participantId === participantId && goalToConfirm?.data?.asyncRequestType === requestType, [goalToConfirm]);

    const createParticipantAsyncRequest = useCallback((participantId: string, requestType: AsyncRequestTypeName) => {
        if (ASYNC_REQUESTS_TO_CONFIRM_ON_MOBILE.includes(requestType) &&
            isMobile() &&
            !isAsyncRequestConfirmGoal(participantId, requestType)
        ) {
            setGoalToConfirm({type: SessionGoalType.AsyncRequest, participantId, data: {asyncRequestType: requestType}});
            return;
        }

        if (requestType === AsyncRequestTypeName.IdAnalyzerFaceVerification) {
            openVerificationScreenshotsModal(participantId, AsyncRequestTypeName.IdAnalyzerFaceVerification);
        } else if (requestType === AsyncRequestTypeName.UploadSelfieVerification) {
            executeUploadSelfieVerification(participantId);
        } else if (requestType === AsyncRequestTypeName.SumaDocumentVerification) {
            openVerificationScreenshotsModal(participantId, AsyncRequestTypeName.SumaDocumentVerification);
        } else {
            sendCreateParticipantAsyncRequest(participantId, requestType);
        }
    }, [isAsyncRequestConfirmGoal, openVerificationScreenshotsModal, executeUploadSelfieVerification,
        sendCreateParticipantAsyncRequest]);

    const resetParticipantAsyncRequest = useCallback((participantId: string, requestType: AsyncRequestTypeName) =>
        handleInSessionActionRequest(
            agentActionsApi.resetParticipantAsyncRequest(sessionParams, {participantId, requestType}),
            InSessionAction.ToggleAsyncRequest,
            {participantId, asyncRequestType: requestType}
        ), [sessionParams, handleInSessionActionRequest]);

    const showParticipantAsyncRequestDetails = useCallback((participantId: string, type: AsyncRequestTypeName) => {
        setAsyncRequestsDialogQueueProps((prev) => [...prev, {type, participantId}]);
    }, []);

    const closeParticipantAsyncRequestDetails = useCallback((data?: {reset: boolean}) => {
        const {participantId, type} = activeAsyncRequestDialog;
        setAsyncRequestsDialogQueueProps((prev) => filter(prev, (item) => item.type !== type || item.participantId !== participantId));
        if (data?.reset) {
            resetParticipantAsyncRequest(participantId, type);
        }
    }, [activeAsyncRequestDialog, resetParticipantAsyncRequest]);

    const fetchAsyncRequestScreenshot = useCallback((participantId: string, requestType: AsyncRequestTypeName) =>
        agentActionsApi.getParticipantAsyncRequestScreenshot(sessionParams, {
            requestType,
            participantId
        }), [sessionParams]);

    useEffect(() => {
        if (!isEmpty(resolvedAsyncRequests)) {
            const mappedRequests = map(resolvedAsyncRequests, ({participantId, request}) => ({participantId, type: request.type}));
            setAsyncRequestsDialogQueueProps((prev) => [...prev, ...mappedRequests]);
            cleanResolvedAsyncRequests();
        }
    }, [resolvedAsyncRequests, cleanResolvedAsyncRequests]);
    /** End of "Async requests" */

    const confirmGoal = useCallback(() => {
        if (!goalToConfirm) {
            return;
        }
        setGoalToConfirm(null);
        if (goalToConfirm.type === SessionGoalType.Screenshot) {
            const screenshot = find(session.screenshots, {name: goalToConfirm.data.screenshotName});
            takeScreenshot(screenshot);
        }
        if (goalToConfirm.type === SessionGoalType.AsyncRequest) {
            createParticipantAsyncRequest(goalToConfirm.participantId, goalToConfirm.data?.asyncRequestType);
        }
    }, [session, goalToConfirm, createParticipantAsyncRequest, takeScreenshot]);

    const setDefaultSidebarView = useCallback((_session: AgentSession) => {
        if (some(_session?.participants, (p) => p.role === 'client' && p.data?.inWidget && textChatActivity)) {
            onSidebarViewChange(SidebarView.TextChat);
        } else if (sipActivity) {
            onSidebarViewChange(SidebarView.SIP);
        }
    }, [textChatActivity, onSidebarViewChange, sipActivity]);

    const handleVideoPinParticipantUpdate = useCallback((_videoPinParticipant: VideoPinParticipant, participantId?: string) => {
        if (participantId) {
            if (pinnedParticipantId !== participantId) {
                setPinnedParticipantId(participantId);
            } else {
                setPinnedParticipantId(null);
            }
        } else {
            if (inSessionDataStore.getValue().videoPinParticipant !== _videoPinParticipant) {
                inSessionDataStore.mergeValue({videoPinParticipant: _videoPinParticipant});
                setVideoPinParticipant(_videoPinParticipant);
            } else {
                inSessionDataStore.mergeValue({videoPinParticipant: null});
                setVideoPinParticipant(null);
            }
        }
    }, [pinnedParticipantId]);

    const updateVirtualBackground = useCallback((config: VirtualBackgroundConfig) => {
        if (!config || isEqual(config, virtualBackgroundConfig)) {
            return;
        }
        setVirtualBackgroundConfig(config);
        return updateVirtualBackgroundSettings(config, sessionParams);
    }, [virtualBackgroundConfig, sessionParams, updateVirtualBackgroundSettings]);

    useUnmount(() => {
        if (!isOpenedInNewWindow && !isEnded && isStarted && !isDeescalated.current) {
            leaveSession();
        }
    });

    useEffect(() => {
        if (!isEmpty(participantsOnlineStatusChanges)) {
            forEach(participantsOnlineStatusChanges, ({current, prev}: {current: boolean, prev: boolean}, participantId: string) => {
                if (!current && !isBoolean(prev)) {
                    return;
                }
                const participant = find(session.participants, {id: participantId});
                // The diff keys come from the previous session snapshot, so an id can already be gone from the
                // current participant list (participants are dropped on a session participants update)
                const participantNames = sessionUtils.getParticipantNames(participant);
                if (!participantNames) {
                    return;
                }
                const {name, firstName, lastName} = participantNames;
                const statusTKey = current ? T_KEYS_ONLINE_STATE.ONLINE : T_KEYS_ONLINE_STATE.OFFLINE;
                notifier.info({
                    IconComponent: (
                        <Initials
                            initials={sessionUtils.getInitials(firstName, lastName)}
                            colorWord=''
                            colored={current}
                            onlineStatus={current ? OnlineStatus.Online : OnlineStatus.Offline}
                        />

                    ),
                    title: name + ' ' + lowerCase(t(statusTKey))
                });
            });
        }
    }, [participantsOnlineStatusChanges, session.participants, t]);

    useEffect(() => {
        if (!initialized) {
            fetchFailedResultValues(session?.assignment, true);
            fetchSuccessResultValues(session?.assignment, true);
            if (virtualBackgroundEnabled) {
                fetchVirtualBackgrounds(sessionParams);
            }
            fetchGroups();
            setDefaultSidebarView(session);
            setInitialized(true);
        }
    }, [initialized,
        session,
        sessionParams,
        virtualBackgroundEnabled,
        fetchFailedResultValues,
        fetchSuccessResultValues,
        fetchGroups,
        setDefaultSidebarView,
        fetchVirtualBackgrounds
    ]);

    useEffect(() => {
        if (goalToConfirm && sidebarView) {
            onSidebarViewChange(null);
        }
    }, [goalToConfirm, sidebarView, onSidebarViewChange]);

    useEffect(() => {
        if (hasUnreadMessages && sidebarView === SidebarView.TextChat) {
            setHasUnreadMessages(false);
        }
    }, [hasUnreadMessages, sidebarView]);

    useEffect(() => {
        if (sidebarView === SidebarView.Transcriptions) {
            if (!audioActivity && !audioVideoActivity) {
                onSidebarViewChange(null);
            }
        }
    }, [sidebarView, audioActivity, audioVideoActivity, onSidebarViewChange]);

    useEffect(() => {
        if (isStarted && isEnded && canSendFeedback && !feedbackModalAutoOpened) {
            setFeedbackModalProps({open: true, sent: false});
            setFeedbackModalAutoOpened(true);
        }
    }, [isStarted, isEnded, canSendFeedback, feedbackModalAutoOpened]);

    useEffect(() => {
        userActiveSessionStore.mergeValue({audioEnabled});
    }, [audioEnabled]);

    useEffect(() => {
        if (isStarted && !prevIsStarted) {
            setInviteModalOpen(false);
        }
    }, [isStarted, prevIsStarted]);

    useEffect(() => {
        if (inviteModalOpen && sendInviteParticipantConnected && !prevSendInviteParticipantConnected) {
            setInviteModalOpen(false);
        }
    }, [inviteModalOpen, sendInviteParticipantConnected, prevSendInviteParticipantConnected]);

    return (
        <UserSessionContext.Provider value={{
            ...contextProps, ...(payload && payload), videoPinParticipant, pinnedParticipantId,
            audioEnabled, videoEnabled, fullScreenVideo, screenshotsPreview, deleteScreenshotInProgress, participantsNetworkQuality,
            videoDevices, audioDevices, goalToConfirm, hasUnreadMessages, feedbackSent: feedbackModalProps.sent, aiSuggestionsLoading,
            groups, textChatThreads, aiSuggestions, aiSuggestionsError: !!aiSuggestionsError,
            subtitlesThreads, virtualBackgroundConfig, aiSuggestionsAudio, aiSuggestionsAudioLoading,
            aiSuggestionsAudioError: !!aiSuggestionsAudioError, tracks, localSessionEnded, deescalated,
            agentChatbotThreads, askAgentChatbot,
            requestAiSuggestionsAudio, requestAiSuggestions, updateTranscriptionSettings,
            onActivityParticipantLanguageChange, virtualBackgroundContent,
            setGoalToConfirm, toggleActivity, reassign, toggleOnHold, requestEndSession, notifyWebRTCStartSuccess, notifyWebRTCStartFailed,
            confirmGoal, setScreenSharingActivityStatus, onToggleAudio, onToggleVideo, onToggleFullScreenVideo, onScreenshotClick,
            onToggleScreenshotsPreview, loadScreenshotPreview, createParticipantAsyncRequest, resetParticipantAsyncRequest,
            showParticipantAsyncRequestDetails, fetchAsyncRequestScreenshot, onRequestDeleteScreenShot, onStopScreenSharing,
            onTextChatNewMessage, onTextChatFileDownload, onTextChatSubmitMessage, onTextChatChangeState, toggleGoalsModal, setTracks,
            onLocalNetworkQualityLevelChange, onRemoteNetworkQualityLevelChange, openInviteModal, setMediaDevices,
            onAudioStreamingToggle, audioStreamState, setAudioStreamState, canMakeScreenshot, toggleFeedbackModal,
            changeAssignment, requestUpdateParticipantDevicePermission,
            setVirtualBackgroundConfig, updateVirtualBackground, leaveSession, onParticipantRemove,
            onParticipantUpdate, setMediaDevicesDialogOpen, onWebRTCConnectError, onTogglePictureInPicture, handleVideoPinParticipantUpdate,
            setDeescalateDialogOpen,
        }}
        >
            {children}
            {!isEnded && !localSessionEnded && <AudioComplianceNotifier key={session?.id} sessionId={session?.id} />}

            <canvas id={SCREENSHOT_CANVAS_ID} className='tw-hidden' />

            <ResultInformationDialog
                {...resultInformationDialogProps}
                onClose={handleResultInformationDialogClose}
            />

            <ScreenSharingDialog
                open={screenSharingDialogOpen}
                session={session}
                onClose={onScreenSharingDialogClose}
            />

            <CoBrowsingStartDialog
                {...coBrowseStartDialogProps}
                clientUrl={clientParticipant?.data?.meta?.urlFrom}
                onClose={onCoBrowsingStartDialogClose}
            />

            <GoalsModal
                open={goalsModalOpen}
                onClose={toggleGoalsModal}
            />

            <ConfirmModal {...confirmStopActivityDialogProps} onClose={onCloseConfirmStopActivityDialog} />

            <ReassignModal
                open={reassignDialogOpen}
                isSipOrPhoneClient={isSipOrPhoneClient}
                groups={groups}
                onClose={onCloseReassignModal}
            />

            <AssignmentSelectionModal
                open={changeAssignmentDialogOpen}
                groups={groups}
                groupDisabled
                loading={changeAssignmentLoading}
                selectedGroupId={session.assignment.groupId}
                selectedSubgroupId={session.assignment.subgroupId}
                onClose={onCloseChangeAssignmentModal}
            />

            <ScreenshotsSelectionModal
                open={createAsyncRequestDialogProps?.type === AsyncRequestTypeName.IdAnalyzerFaceVerification}
                sections={[
                    {
                        label: t(T_KEYS.VERIFICATION_SCREENSHOTS_SELECTION_MODAL.DOCUMENT_IMAGE),
                        key: 'selectedDocumentScreenshot',
                        required: true
                    },
                    {
                        label: t(T_KEYS.VERIFICATION_SCREENSHOTS_SELECTION_MODAL.FACE_IMAGE),
                        key: 'selectedSelfieScreenshot',
                        required: true
                    }
                ]}
                onClose={(confirmed: boolean, params: any) =>
                    onCloseVerificationScreenshotsModal(confirmed, params, AsyncRequestTypeName.IdAnalyzerFaceVerification)}
            />

            <ScreenshotsSelectionModal
                open={createAsyncRequestDialogProps?.type === AsyncRequestTypeName.SumaDocumentVerification}
                sections={[
                    {
                        label: t(T_KEYS.VERIFICATION_SCREENSHOTS_SELECTION_MODAL.DOCUMENT_FRONT_IMAGE),
                        key: 'selectedDocumentFrontScreenshot',
                        required: true,
                    },
                    {
                        label: t(T_KEYS.VERIFICATION_SCREENSHOTS_SELECTION_MODAL.DOCUMENT_BACK_IMAGE),
                        key: 'selectedDocumentBackScreenshot'
                    },
                    {
                        label: t(T_KEYS.VERIFICATION_SCREENSHOTS_SELECTION_MODAL.FACE_IMAGE),
                        key: 'selectedSelfieScreenshot'
                    }
                ]}
                onClose={(confirmed: boolean, params: any) =>
                    onCloseVerificationScreenshotsModal(confirmed, params, AsyncRequestTypeName.SumaDocumentVerification)}
            />

            <UploadSelfieVerificationDialog
                open={activeAsyncRequestDialog?.type === AsyncRequestTypeName.UploadSelfieVerification}
                asyncRequest={activeAsyncRequest}
                onClose={closeParticipantAsyncRequestDetails}
            />

            <IdAnalyzerFaceVerificationModal
                open={activeAsyncRequestDialog?.type === AsyncRequestTypeName.IdAnalyzerFaceVerification}
                asyncRequest={activeAsyncRequest}
                onClose={closeParticipantAsyncRequestDetails}
            />

            <SumaDocumentVerificationModal
                open={activeAsyncRequestDialog?.type === AsyncRequestTypeName.SumaDocumentVerification ||
                    activeAsyncRequestDialog?.type === AsyncRequestTypeName.SumaDocumentVerificationSDK}
                asyncRequest={activeAsyncRequest}
                onClose={closeParticipantAsyncRequestDetails}
            />

            <SumaIPVerificationModal
                open={activeAsyncRequestDialog?.type === AsyncRequestTypeName.SumaIPVerification}
                asyncRequest={activeAsyncRequest}
                onClose={closeParticipantAsyncRequestDetails}
            />

            <SumaEmailVerificationModal
                open={activeAsyncRequestDialog?.type === AsyncRequestTypeName.SumaEmailVerification}
                asyncRequest={activeAsyncRequest}
                onClose={closeParticipantAsyncRequestDetails}
            />

            <SumaPhoneVerificationModal
                open={activeAsyncRequestDialog?.type === AsyncRequestTypeName.SumaPhoneVerification}
                asyncRequest={activeAsyncRequest}
                onClose={closeParticipantAsyncRequestDetails}
            />

            <ParticipantGeolocationDialog
                open={activeAsyncRequestDialog?.type === AsyncRequestTypeName.Geolocation}
                participantId={activeAsyncRequestDialog?.participantId}
                asyncRequest={activeAsyncRequest}
                fetchAsyncRequestScreenshot={fetchAsyncRequestScreenshot}
                onClose={closeParticipantAsyncRequestDetails}
            />

            <ScreenshotPreviewModal screenshot={selectedScreenshot} onClose={onCloseScreenshotPreviewModal} />

            <ConflictingActivitiesModal
                {...conflictingActivitiesModalProps}
                onClose={handleConflictingActivitiesModalClose}
            />

            <ConfirmModal
                open={!!deleteScreenshotCandidate}
                title={t(T_KEYS.DELETE_SCREENSHOT_CONFIRM_DIALOG.TITLE)}
                content={deleteScreenshotCandidate?.name}
                onClose={onCloseDeleteScreenshotDialog}
            />

            <ClientDataModal
                key={updateParticipantModalProps?.participant?.id}
                open={updateParticipantModalProps?.open}
                loading={updateParticipantLoading}
                participant={updateParticipantModalProps?.participant as InviteeParticipant}
                guestEnabled={guestEnabled}
                basePath='userInfo'
                error={updateParticipantError}
                assignment={{groupId: session.assignment.groupId, subgroupId: session.assignment.subgroupId}}
                onClose={onCloseUpdateParticipantModal}
            />

            <InviteModal
                open={inviteModalOpen}
                session={session}
                participantId={sendInviteModalParticipant?.id}
                onParticipantInvite={handleParticipantInvite}
                onClose={() => setInviteModalOpen(false)}
            />

            <SessionFeedbackModal
                open={feedbackModalProps.open}
                loading={addFeedbackLoading}
                availability={feedbackAvailability}
                onClose={onCloseFeedbackModal}
            />

            {mediaDevicesUtils.canChangeMediaSettings() &&
                <MediaDevicesDialog
                    open={mediaDevicesDialogOpen}
                    mediaDevices={currentMediaDevices}
                    onClose={() => setMediaDevicesDialogOpen(false)}
                    onChange={setCurrentMediaDevices}
                />}

            {canManageSession &&
                <DeescalateDialog
                    open={deescalateDialogOpen}
                    loading={isDeescalating}
                    onClose={deescalate}
                />}
            <PictureInPictureObserver />
        </UserSessionContext.Provider>
    );
};
